/**
 * Comprehensive Currency Formatter
 * Uses existing country_code_currencies.json + native Intl API
 * Supports ALL ISO 4217 currencies (170+)
 */

import currencyService from '../Service/currency.service';

export interface CurrencyInfo {
  code: string;              // ISO 4217 code (USD, INR, EUR, etc.)
  symbol: string;            // Currency symbol (₹, $, €, etc.)
  name: string;              // Full name (US Dollar, Indian Rupee, etc.)
  decimalPlaces: number;     // Decimal places (usually 2)
  suffix?: string;           // Custom suffix (LPA for INR)
  locale: string;           // Locale for formatting (en-IN, en-US, etc.)
  format: 'lakh' | 'thousand' | 'million' | 'absolute'; // Format preference
}

// Cache for currency data
let currencyDataCache: any[] | null = null;
let currencyInfoCache: Map<string, CurrencyInfo> = new Map();

/**
 * Load currency data from JSON file (cached)
 */
export const loadCurrencyData = async (): Promise<any[]> => {
  if (currencyDataCache) {
    return currencyDataCache;
  }
  
  try {
    currencyDataCache = await currencyService.getAllCurrencies();
    return currencyDataCache;
  } catch (error) {
    console.error('Error loading currency data:', error);
    return [];
  }
};

/**
 * Get currency code from country code using JSON
 */
export const getCurrencyFromCountry = async (
  countryCode: string
): Promise<string | null> => {
  const data = await loadCurrencyData();
  const country = data.find(
    (item: any) => item.country_code?.toUpperCase() === countryCode?.toUpperCase()
  );
  return country?.currency_code || null;
};

/**
 * Detect currency from browser locale
 */
export const detectCurrencyFromBrowser = async (): Promise<string> => {
  try {
    const locale = navigator.language || 
                   (Intl.DateTimeFormat().resolvedOptions()?.locale as string) ||
                   'en-US';
    
    // Try to extract country code from locale (e.g., 'en-IN' -> 'IN')
    const countryCode = locale.split('-')[1]?.toUpperCase();
    
    if (countryCode) {
      const currency = await getCurrencyFromCountry(countryCode);
      if (currency) {
        return currency;
      }
    }
    
    // Fallback: Map common locales to currencies
    const localeCurrencyMap: Record<string, string> = {
      'en-IN': 'INR',
      'hi-IN': 'INR',
      'en-US': 'USD',
      'es-US': 'USD',
      'en-GB': 'GBP',
      'de': 'EUR',
      'fr': 'EUR',
      'it': 'EUR',
      'es': 'EUR',
      'ja': 'JPY',
      'zh': 'CNY',
      'en-AU': 'AUD',
      'en-CA': 'CAD',
    };
    
    for (const [key, currency] of Object.entries(localeCurrencyMap)) {
      if (locale.startsWith(key)) {
        return currency;
      }
    }
    
    return 'USD'; // Final fallback
  } catch (error) {
    console.error('Error detecting currency:', error);
    return 'USD';
  }
};

/**
 * Get currency symbol using native Intl API (supports all currencies)
 */
export const getCurrencySymbol = (currencyCode: string, locale?: string): string => {
  try {
    // Use Intl.NumberFormat to get the symbol
    const formatter = new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    
    // Extract symbol from formatted string
    const parts = formatter.formatToParts(0);
    const symbolPart = parts.find(part => part.type === 'currency');
    return symbolPart?.value || currencyCode;
  } catch (error) {
    // Fallback for unsupported currencies
    const fallbackSymbols: Record<string, string> = {
      'INR': '₹',
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'JPY': '¥',
      'CNY': '¥',
      'AUD': 'A$',
      'CAD': 'C$',
      'SGD': 'S$',
      'AED': 'د.إ',
      'SAR': 'ر.س',
      'ZAR': 'R',
      'BRL': 'R$',
      'MXN': '$',
    };
    return fallbackSymbols[currencyCode] || currencyCode;
  }
};

/**
 * Get locale for currency formatting
 */
export const getCurrencyLocale = (currencyCode: string, countryCode?: string): string => {
  if (countryCode) {
    // Try to construct locale from country code
    return `en-${countryCode}`;
  }
  
  // Map common currencies to locales
  const currencyLocaleMap: Record<string, string> = {
    'INR': 'en-IN',
    'USD': 'en-US',
    'EUR': 'en-GB',
    'GBP': 'en-GB',
    'JPY': 'ja-JP',
    'CNY': 'zh-CN',
    'AUD': 'en-AU',
    'CAD': 'en-CA',
    'SGD': 'en-SG',
    'AED': 'ar-AE',
    'SAR': 'ar-SA',
    'ZAR': 'en-ZA',
    'BRL': 'pt-BR',
    'MXN': 'es-MX',
  };
  
  return currencyLocaleMap[currencyCode] || 'en-US';
};

/**
 * Get currency name
 */
export const getCurrencyName = (currencyCode: string): string => {
  try {
    // Try to use Intl.DisplayNames if available (modern browsers)
    if ('DisplayNames' in Intl) {
      const displayNames = new (Intl as any).DisplayNames(['en'], { type: 'currency' });
      return displayNames.of(currencyCode) || currencyCode;
    }
  } catch (error) {
    // Fallback to manual mapping
  }
  
  // Manual mapping for common currencies
  const names: Record<string, string> = {
    'INR': 'Indian Rupee',
    'USD': 'US Dollar',
    'EUR': 'Euro',
    'GBP': 'British Pound',
    'JPY': 'Japanese Yen',
    'CNY': 'Chinese Yuan',
    'AUD': 'Australian Dollar',
    'CAD': 'Canadian Dollar',
    'SGD': 'Singapore Dollar',
    'AED': 'UAE Dirham',
    'SAR': 'Saudi Riyal',
    'ZAR': 'South African Rand',
    'BRL': 'Brazilian Real',
    'MXN': 'Mexican Peso',
    'CHF': 'Swiss Franc',
    'NZD': 'New Zealand Dollar',
    'HKD': 'Hong Kong Dollar',
    'KRW': 'South Korean Won',
    'THB': 'Thai Baht',
    'MYR': 'Malaysian Ringgit',
    'IDR': 'Indonesian Rupiah',
    'PHP': 'Philippine Peso',
    'VND': 'Vietnamese Dong',
  };
  
  return names[currencyCode] || currencyCode;
};

/**
 * Get comprehensive currency info (async)
 */
export const getCurrencyInfo = async (
  currencyCode: string,
  countryCode?: string
): Promise<CurrencyInfo> => {
  // Check cache first
  const cacheKey = `${currencyCode}_${countryCode || 'default'}`;
  if (currencyInfoCache.has(cacheKey)) {
    return currencyInfoCache.get(cacheKey)!;
  }
  
  const locale = getCurrencyLocale(currencyCode, countryCode);
  const symbol = getCurrencySymbol(currencyCode, locale);
  const name = getCurrencyName(currencyCode);
  
  // Determine format preference and decimal places
  let format: 'lakh' | 'thousand' | 'million' | 'absolute' = 'thousand';
  let suffix: string | undefined;
  let decimalPlaces = 2;
  
  if (currencyCode === 'INR') {
    format = 'lakh';
    suffix = 'LPA';
    decimalPlaces = 2;
  } else if (['JPY', 'KRW', 'VND'].includes(currencyCode)) {
    // These currencies typically don't use decimals
    decimalPlaces = 0;
    format = 'absolute';
  } else if (['USD', 'EUR', 'GBP', 'AUD', 'CAD'].includes(currencyCode)) {
    format = 'thousand';
    decimalPlaces = 2;
  } else {
    format = 'thousand';
    decimalPlaces = 2;
  }
  
  const info: CurrencyInfo = {
    code: currencyCode,
    symbol,
    name,
    decimalPlaces,
    suffix,
    locale,
    format,
  };
  
  // Cache it
  currencyInfoCache.set(cacheKey, info);
  
  return info;
};

/**
 * Get currency config (sync version for immediate use)
 */
export const getCurrencyConfig = (currencyCode?: string): Partial<CurrencyInfo> => {
  if (!currencyCode) {
    currencyCode = 'INR'; // Default
  }

  const symbol = getCurrencySymbol(currencyCode);
  const locale = getCurrencyLocale(currencyCode);

  return {
    code: currencyCode,
    symbol,
    locale,
    suffix: currencyCode === 'INR' ? 'LPA' : undefined,
    format: currencyCode === 'INR' ? 'lakh' : 'thousand',
    decimalPlaces: ['JPY', 'KRW', 'VND'].includes(currencyCode) ? 0 : 2,
  };
};

/**
 * Parse currency input (handles multiple formats)
 * Supports: 2.6, 260000, 2,60,000 for INR
 */
export const parseCurrencyInput = (
  value: string | number | null | undefined,
  currencyCode: string = 'INR'
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  // Remove currency symbols, spaces, and common suffixes
  let cleaned = String(value)
    .trim()
    .toUpperCase()
    .replace(/[₹$€£¥,\s]/g, '') // Remove currency symbols and commas
    .replace(/LPA|LAKHS?|THOUSANDS?|MILLIONS?|CRORES?|CR/gi, '') // Remove text suffixes
    .trim();

  if (cleaned === '') {
    return null;
  }

  const parsed = parseFloat(cleaned);

  if (isNaN(parsed)) {
    return null;
  }

  // For INR: Convert absolute values to lakhs
  if (currencyCode === 'INR') {
    if (parsed > 100 && parsed < 1000000) {
      return parsed / 100000; // Convert thousands to lakhs (260000 -> 2.6)
    }
    if (parsed >= 1000000) {
      return parsed / 100000; // Convert absolute to lakhs
    }
    return parsed; // Already in lakhs format (2.6, 10.5, etc.)
  }

  // For other currencies, return as is
  return parsed;
};

/**
 * Format currency for display using native Intl API
 */
export const formatCurrencyDisplay = (
  value: number | null | undefined,
  currencyCode: string = 'INR',
  options?: {
    showDecimals?: boolean;
    compact?: boolean;
    absoluteValue?: boolean;
    locale?: string;
  }
): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '';
  }

  const config = getCurrencyConfig(currencyCode);
  const { showDecimals = true, compact = false, absoluteValue = false } = options || {};
  const locale = options?.locale || config.locale || 'en-US';

  let displayValue = value;

  // Note: We store absolute values, so no conversion needed
  // The absoluteValue option is kept for backward compatibility but not used

  try {
    // Use native Intl for formatting (supports ALL ISO 4217 currencies)
    // For INR, use 'en-IN' locale for proper Indian numbering system (lakhs/crores)
    const formatLocale = currencyCode === 'INR' ? 'en-IN' : locale;
    const formatter = new Intl.NumberFormat(formatLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: showDecimals ? (config.decimalPlaces || 2) : 0,
      maximumFractionDigits: showDecimals ? (config.decimalPlaces || 2) : 0,
      notation: compact ? 'compact' : 'standard',
    });

    let formatted = formatter.format(displayValue);

    // For INR: Add LPA suffix if not in absolute mode
    if (currencyCode === 'INR' && !absoluteValue && config.suffix) {
      // Remove default currency code if present and add our suffix
      formatted = formatted.replace(` ${currencyCode}`, '') + ` ${config.suffix}`;
    }

    return formatted;
  } catch (error) {
    // Fallback formatting
    const symbol = config.symbol || currencyCode;
    const formattedNumber = new Intl.NumberFormat(locale, {
      minimumFractionDigits: showDecimals ? (config.decimalPlaces || 2) : 0,
      maximumFractionDigits: showDecimals ? (config.decimalPlaces || 2) : 0,
    }).format(displayValue);
    
    return `${symbol}${formattedNumber}${config.suffix ? ' ' + config.suffix : ''}`;
  }
};

/**
 * Format currency for input field
 */
export const formatCurrencyInput = (
  value: number | string | null | undefined,
  currencyCode: string = 'INR'
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numValue = typeof value === 'number' ? value : parseFloat(String(value));

  if (isNaN(numValue)) {
    return '';
  }

  const config = getCurrencyConfig(currencyCode);

  // For INR: Show in lakhs format by default
  if (currencyCode === 'INR') {
    if (numValue >= 1000) {
      // Format as absolute with Indian numbering: 2,60,000
      return new Intl.NumberFormat('en-IN').format(numValue);
    }
    // Otherwise show as lakhs: 2.6
    return numValue.toFixed(2).replace(/\.?0+$/, '');
  }

  // For other currencies: Use standard thousand separator
  return new Intl.NumberFormat(config.locale || 'en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numValue);
};

/**
 * Validate currency input
 */
export const validateCurrencyInput = (
  value: string | number,
  currencyCode: string = 'INR'
): { valid: boolean; error?: string; normalizedValue?: number } => {
  const parsed = parseCurrencyInput(value, currencyCode);

  if (parsed === null) {
    return { valid: false, error: 'Please enter a valid amount' };
  }

  if (parsed < 0) {
    return { valid: false, error: 'Amount cannot be negative' };
  }

  // Reasonable upper limits (currency-specific)
  const limits: Record<string, number> = {
    'INR': 1000, // 1000 LPA
    'USD': 1000000, // $1M
    'EUR': 1000000, // €1M
    'GBP': 1000000, // £1M
    'JPY': 100000000, // ¥100M
    'CNY': 10000000, // ¥10M
  };

  const limit = limits[currencyCode] || 10000000;

  if (parsed > limit) {
    return {
      valid: false,
      error: `Amount seems unusually high. Please verify (max ${formatCurrencyDisplay(limit, currencyCode)})`,
    };
  }

  return { valid: true, normalizedValue: parsed };
};

/**
 * Get currency options for dropdown with country names
 */
export const getCurrencyOptionsForDropdown = async (): Promise<Array<{
  label: string;
  value: string;
  country?: string;
}>> => {
  const data = await loadCurrencyData();
  const currencyMap = new Map<string, { countries: string[]; symbol: string; name: string }>();

  // Preferred countries for common currencies (will be shown first)
  const preferredCountries: Record<string, string> = {
    'USD': 'United States',
    'EUR': 'Germany', // Euro - using Germany as primary
    'GBP': 'United Kingdom',
    'INR': 'India',
    'AUD': 'Australia',
    'CAD': 'Canada',
    'SGD': 'Singapore',
    'JPY': 'Japan',
    'CNY': 'China',
  };

  // Group by currency code
  data.forEach((item: any) => {
    const code = item.currency_code;
    const country = item.country || item.country_name || item.country_code;
    
    if (code) {
      if (!currencyMap.has(code)) {
        currencyMap.set(code, {
          countries: [],
          symbol: getCurrencySymbol(code),
          name: getCurrencyName(code),
        });
      }
      const entry = currencyMap.get(code)!;
      if (country && !entry.countries.includes(country)) {
        entry.countries.push(country);
      }
    }
  });

  // Sort countries within each currency: preferred first, then alphabetically
  currencyMap.forEach((entry, code) => {
    const preferred = preferredCountries[code];
    entry.countries.sort((a, b) => {
      // Put preferred country first
      if (a === preferred) return -1;
      if (b === preferred) return 1;
      // Then sort alphabetically
      return a.localeCompare(b);
    });
  });

  // Create options
  const options: Array<{ label: string; value: string; country?: string }> = [];
  
  // Common currencies first
  const commonCurrencies = ['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD', 'SGD', 'JPY', 'CNY'];
  commonCurrencies.forEach(code => {
    const entry = currencyMap.get(code);
    if (entry && entry.countries.length > 0) {
      // Use first country (now sorted with preferred first)
      const primaryCountry = entry.countries[0];
      options.push({
        label: `${primaryCountry} (${code}) ${entry.symbol}`,
        value: code,
        country: primaryCountry,
      });
      currencyMap.delete(code);
    }
  });

  // Add remaining currencies sorted by primary country name
  Array.from(currencyMap.entries())
    .filter(([, entry]) => entry.countries.length > 0)
    .sort((a, b) => {
      const countryA = a[1].countries[0];
      const countryB = b[1].countries[0];
      return countryA.localeCompare(countryB);
    })
    .forEach(([code, entry]) => {
      const primaryCountry = entry.countries[0];
      options.push({
        label: `${primaryCountry} (${code}) ${entry.symbol}`,
        value: code,
        country: primaryCountry,
      });
    });

  return options;
};

